/**
 * Location-Based Scheduling flowline chart.
 * Y-axis: locations (hierarchical) with buildings as header rows.
 * X-axis: 3-level time header (month → week → day).
 * Diagonal lines connect section/PP work as it flows through locations.
 */

import { useMemo, useRef } from "react";
import {
  type Flowline,
  type LocationRow,
} from "./chart-utils";
import { ChevronRightIcon, ChevronDownIcon } from "@/lib/icons";

const LABEL_WIDTH = 240;
const ROW_HEIGHT = 28;
const MONTH_HEADER_HEIGHT = 24;
const WEEK_HEADER_HEIGHT = 24;
const DAY_HEADER_HEIGHT = 24;
const TOTAL_HEADER_HEIGHT = MONTH_HEADER_HEIGHT + WEEK_HEADER_HEIGHT + DAY_HEADER_HEIGHT;
const MIN_CHART_WIDTH = 800;
const DAY_WIDTH = 40; // pixels per day

interface LBSChartProps {
  locationRows: LocationRow[];
  flowlines: Flowline[];
  dateRange: { start: Date; end: Date };
  selectedFlowline: string | null;
  onSelectFlowline: (id: string | null) => void;
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
  onToggleExpandAll: (id: string) => void;
  minHeight: number;
}

// ---- Time header helpers ----

interface DayColumn {
  date: Date;
  x: number;
  label: string; // "Mon, 5/4"
  isWeekend: boolean;
}

interface WeekSpan {
  weekNum: number;
  x: number;
  width: number;
}

interface MonthSpan {
  label: string;
  x: number;
  width: number;
}

function generateDayColumns(start: Date, end: Date): DayColumn[] {
  const days: DayColumn[] = [];
  const current = new Date(start);
  current.setHours(0, 0, 0, 0);

  let i = 0;
  while (current <= end) {
    const dayOfWeek = current.getDay();
    const dayNames = ["Su", "Ma", "Ti", "Ke", "To", "Pe", "La"];
    days.push({
      date: new Date(current),
      x: i * DAY_WIDTH,
      label: `${dayNames[dayOfWeek]}, ${current.getDate()}.${current.getMonth() + 1}`,
      isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
    });
    current.setDate(current.getDate() + 1);
    i++;
  }
  return days;
}

function generateWeekSpans(days: DayColumn[]): WeekSpan[] {
  const spans: WeekSpan[] = [];
  let currentWeek = -1;
  let spanStart = 0;

  for (let i = 0; i < days.length; i++) {
    const d = days[i].date;
    const iso = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    iso.setUTCDate(iso.getUTCDate() + 4 - (iso.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(iso.getUTCFullYear(), 0, 1));
    const weekNum = Math.ceil(((iso.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);

    if (weekNum !== currentWeek) {
      if (currentWeek !== -1) {
        spans.push({
          weekNum: currentWeek,
          x: spanStart,
          width: i * DAY_WIDTH - spanStart,
        });
      }
      currentWeek = weekNum;
      spanStart = i * DAY_WIDTH;
    }
  }
  if (currentWeek !== -1) {
    spans.push({
      weekNum: currentWeek,
      x: spanStart,
      width: days.length * DAY_WIDTH - spanStart,
    });
  }
  return spans;
}

function generateMonthSpans(days: DayColumn[]): MonthSpan[] {
  const spans: MonthSpan[] = [];
  let currentKey = "";
  let spanStart = 0;
  const monthNames = [
    "Tammikuu", "Helmikuu", "Maaliskuu", "Huhtikuu", "Toukokuu", "Kesäkuu",
    "Heinäkuu", "Elokuu", "Syyskuu", "Lokakuu", "Marraskuu", "Joulukuu",
  ];

  for (let i = 0; i < days.length; i++) {
    const d = days[i].date;
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    if (key !== currentKey) {
      if (currentKey !== "") {
        const prevDate = days[i - 1].date;
        spans.push({
          label: `${monthNames[prevDate.getMonth()]} ${prevDate.getFullYear()}`,
          x: spanStart,
          width: i * DAY_WIDTH - spanStart,
        });
      }
      currentKey = key;
      spanStart = i * DAY_WIDTH;
    }
  }
  if (currentKey !== "" && days.length > 0) {
    const lastDate = days[days.length - 1].date;
    spans.push({
      label: `${monthNames[lastDate.getMonth()]} ${lastDate.getFullYear()}`,
      x: spanStart,
      width: days.length * DAY_WIDTH - spanStart,
    });
  }
  return spans;
}

export function LBSChart({
  locationRows,
  flowlines,
  dateRange,
  selectedFlowline,
  onSelectFlowline,
  expandedIds,
  onToggleExpand,
  onToggleExpandAll,
  minHeight,
}: LBSChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Generate day-based columns
  const dayColumns = useMemo(
    () => generateDayColumns(dateRange.start, dateRange.end),
    [dateRange],
  );
  const weekSpans = useMemo(() => generateWeekSpans(dayColumns), [dayColumns]);
  const monthSpans = useMemo(() => generateMonthSpans(dayColumns), [dayColumns]);

  const chartWidth = Math.max(MIN_CHART_WIDTH, dayColumns.length * DAY_WIDTH);
  const dataRowCount = locationRows.length;
  const availableForRows = minHeight - TOTAL_HEADER_HEIGHT;
  const minRowCount = availableForRows > 0 ? Math.ceil(availableForRows / ROW_HEIGHT) : dataRowCount;
  const totalRowCount = Math.max(dataRowCount, minRowCount);
  const chartHeight = totalRowCount * ROW_HEIGHT;
  const totalHeight = chartHeight + TOTAL_HEADER_HEIGHT;

  const todayX = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const startMs = dateRange.start.getTime();
    const dayMs = 86400000;
    const dayIndex = Math.floor((now.getTime() - startMs) / dayMs);
    return dayIndex * DAY_WIDTH + DAY_WIDTH / 2;
  }, [dateRange]);
  const showToday = todayX >= 0 && todayX <= chartWidth;

  // Lookup from location ID to visible row
  const rowMap = useMemo(() => {
    const m = new Map<string, LocationRow>();
    for (const r of locationRows) m.set(r.id, r);
    return m;
  }, [locationRows]);

  // Convert date to pixel X using day columns
  const dateToX = (date: Date): number => {
    const startMs = dateRange.start.getTime();
    const diffDays = (date.getTime() - startMs) / 86400000;
    return diffDays * DAY_WIDTH;
  };

  return (
    <div ref={containerRef} className="flex overflow-x-auto">
      {/* Location labels (Y-axis) */}
      <div className="shrink-0 border-r bg-background sticky left-0 z-10" style={{ width: LABEL_WIDTH }}>
        {/* Header spacer */}
        <div className="border-b" style={{ height: TOTAL_HEADER_HEIGHT }} />

        {locationRows.map((row) => {
          const isBuilding = row.locationType === "BUILDING";

          return (
            <div
              key={row.id}
              className={`border-b flex items-center text-sm truncate ${
                isBuilding
                  ? "bg-muted/60 font-semibold px-3"
                  : "hover:bg-accent/30 px-2"
              }`}
              style={{
                height: ROW_HEIGHT,
                paddingLeft: isBuilding ? "8px" : `${20 + (row.depth - 1) * 16}px`,
              }}
            >
              {row.hasChildren ? (
                <button
                  onClick={(e) => {
                    if (e.ctrlKey || e.metaKey) {
                      onToggleExpandAll(row.id);
                    } else {
                      onToggleExpand(row.id);
                    }
                  }}
                  className="p-0.5 rounded hover:bg-accent mr-1 shrink-0"
                  title="Ctrl+klikkaa avataksesi kaikki"
                >
                  {expandedIds.has(row.id) ? (
                    <ChevronDownIcon className="h-3 w-3" />
                  ) : (
                    <ChevronRightIcon className="h-3 w-3" />
                  )}
                </button>
              ) : (
                <span className="w-4 shrink-0" />
              )}
              <span className="truncate">{row.name}</span>
            </div>
          );
        })}
        {/* Empty filler rows */}
        {Array.from({ length: totalRowCount - dataRowCount }, (_, i) => (
          <div
            key={`empty-${i}`}
            className="border-b"
            style={{ height: ROW_HEIGHT }}
          />
        ))}
      </div>

      {/* Chart area */}
      <div className="flex-1 min-w-0">
        <svg
          width={chartWidth}
          height={totalHeight}
          className="block"
        >
          {/* === 3-level time header === */}

          {/* Month row */}
          {monthSpans.map((span, i) => (
            <g key={`month-${i}`}>
              <rect
                x={span.x}
                y={0}
                width={span.width}
                height={MONTH_HEADER_HEIGHT}
                fill="currentColor"
                fillOpacity={0.03}
                stroke="currentColor"
                strokeOpacity={0.15}
                strokeWidth={0.5}
              />
              <text
                x={span.x + 6}
                y={MONTH_HEADER_HEIGHT - 7}
                className="fill-foreground"
                fontSize={11}
                fontWeight={600}
              >
                {span.label}
              </text>
            </g>
          ))}

          {/* Week row */}
          {weekSpans.map((span, i) => (
            <g key={`week-${i}`}>
              <rect
                x={span.x}
                y={MONTH_HEADER_HEIGHT}
                width={span.width}
                height={WEEK_HEADER_HEIGHT}
                fill="currentColor"
                fillOpacity={i % 2 === 0 ? 0.02 : 0.05}
                stroke="currentColor"
                strokeOpacity={0.15}
                strokeWidth={0.5}
              />
              <text
                x={span.x + span.width / 2}
                y={MONTH_HEADER_HEIGHT + WEEK_HEADER_HEIGHT - 7}
                className="fill-foreground"
                fontSize={10}
                fontWeight={500}
                textAnchor="middle"
              >
                Vko {span.weekNum}
              </text>
            </g>
          ))}

          {/* Day row */}
          {dayColumns.map((day, i) => (
            <g key={`day-${i}`}>
              <rect
                x={day.x}
                y={MONTH_HEADER_HEIGHT + WEEK_HEADER_HEIGHT}
                width={DAY_WIDTH}
                height={DAY_HEADER_HEIGHT}
                fill={day.isWeekend ? "currentColor" : "transparent"}
                fillOpacity={day.isWeekend ? 0.06 : 0}
                stroke="currentColor"
                strokeOpacity={0.1}
                strokeWidth={0.5}
              />
              <text
                x={day.x + DAY_WIDTH / 2}
                y={MONTH_HEADER_HEIGHT + WEEK_HEADER_HEIGHT + DAY_HEADER_HEIGHT - 7}
                className="fill-muted-foreground"
                fontSize={8}
                textAnchor="middle"
              >
                {day.label}
              </text>
            </g>
          ))}

          {/* Day vertical grid lines extending into chart area */}
          {dayColumns.map((day, i) => (
            <line
              key={`grid-${i}`}
              x1={day.x}
              y1={TOTAL_HEADER_HEIGHT}
              x2={day.x}
              y2={totalHeight}
              stroke="currentColor"
              strokeOpacity={day.isWeekend ? 0.08 : 0.04}
            />
          ))}

          {/* Week boundary lines (stronger) */}
          {weekSpans.map((span, i) => (
            <line
              key={`weekline-${i}`}
              x1={span.x}
              y1={TOTAL_HEADER_HEIGHT}
              x2={span.x}
              y2={totalHeight}
              stroke="currentColor"
              strokeOpacity={0.15}
            />
          ))}

          {/* Header bottom separator */}
          <line
            x1={0}
            y1={TOTAL_HEADER_HEIGHT}
            x2={chartWidth}
            y2={TOTAL_HEADER_HEIGHT}
            stroke="currentColor"
            strokeOpacity={0.2}
          />

          {/* Row grid lines + building row backgrounds */}
          {Array.from({ length: totalRowCount }, (_, i) => {
            const row = i < dataRowCount ? locationRows[i] : null;
            const isBuilding = row?.locationType === "BUILDING";
            return (
              <g key={row?.id ?? `empty-${i}`}>
                {isBuilding && (
                  <rect
                    x={0}
                    y={TOTAL_HEADER_HEIGHT + i * ROW_HEIGHT}
                    width={chartWidth}
                    height={ROW_HEIGHT}
                    fill="currentColor"
                    fillOpacity={0.04}
                  />
                )}
                <line
                  x1={0}
                  y1={TOTAL_HEADER_HEIGHT + (i + 1) * ROW_HEIGHT}
                  x2={chartWidth}
                  y2={TOTAL_HEADER_HEIGHT + (i + 1) * ROW_HEIGHT}
                  stroke="currentColor"
                  strokeOpacity={isBuilding ? 0.15 : 0.05}
                />
              </g>
            );
          })}

          {/* Weekend column shading */}
          {dayColumns
            .filter((d) => d.isWeekend)
            .map((day, i) => (
              <rect
                key={`weekend-${i}`}
                x={day.x}
                y={TOTAL_HEADER_HEIGHT}
                width={DAY_WIDTH}
                height={chartHeight}
                fill="currentColor"
                fillOpacity={0.03}
              />
            ))}

          {/* Today line */}
          {showToday && (
            <line
              x1={todayX}
              y1={0}
              x2={todayX}
              y2={totalHeight}
              stroke="#ef4444"
              strokeWidth={1.5}
              strokeDasharray="4 3"
            />
          )}

          {/* Flowlines — only horizontal bars where actual work exists */}
          {flowlines.map((fl) => {
            const isSelected = selectedFlowline === fl.id;
            const isDimmed =
              selectedFlowline !== null && selectedFlowline !== fl.id;

            const visiblePoints = fl.points.filter((pt) => rowMap.has(pt.locationId));
            if (visiblePoints.length === 0) return null;

            return (
              <g
                key={fl.id}
                onClick={() => onSelectFlowline(isSelected ? null : fl.id)}
                className="cursor-pointer"
                opacity={isDimmed ? 0.15 : 1}
              >
                {visiblePoints.map((pt, i) => {
                  const row = rowMap.get(pt.locationId)!;
                  const y = TOTAL_HEADER_HEIGHT + row.y + ROW_HEIGHT / 2;
                  const xStart = dateToX(pt.startDate);
                  // End date is inclusive — extend to end of that day
                  const xEnd = dateToX(pt.endDate) + DAY_WIDTH;
                  const width = Math.max(DAY_WIDTH, xEnd - xStart);

                  return (
                    <g key={`${pt.locationId}-${i}`}>
                      <line
                        x1={xStart}
                        y1={y}
                        x2={xStart + width}
                        y2={y}
                        stroke={fl.color}
                        strokeWidth={isSelected ? 3 : 2}
                        strokeLinecap="round"
                      />
                      {/* Label above the line */}
                      {width > 20 && (
                        <text
                          x={xStart + width / 2}
                          y={y - 5}
                          fontSize={9}
                          fill={fl.color}
                          fontWeight={600}
                          fontStyle="italic"
                          textAnchor="middle"
                        >
                          {fl.label}
                        </text>
                      )}
                    </g>
                  );
                })}
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}
